import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from '@brg/ui';
import { API, Endpoints } from '@brg/api';
import { VIRTUAL_SPACE_TAG_ID, UploadedFiles, PLATFORMS } from './constants';
import { useFileUpload } from './useFileUpload';
import { useGetProjectPackages } from './useGetProjectPackages';

interface UseRoomLaunchProps {
  selectedTags: string[];
  projectId?: string;
  onRequiredFieldsChange?: (isFilled: boolean) => void;
  onActiveUploadsChange?: (hasActiveUploads: boolean) => void;
}

export const useRoomLaunch = ({
  selectedTags,
  projectId,
  onRequiredFieldsChange,
  onActiveUploadsChange,
}: UseRoomLaunchProps) => {
  const { t } = useTranslation();
  const toasts = useToasts();
  
  const { uploadProgress, hasActiveUploads, handleFileUpload: uploadFile, handleCancelUpload } = useFileUpload();
  
  const unityInputRef = useRef<HTMLInputElement>(null);
  const windowsInputRef = useRef<HTMLInputElement>(null);
  const macosInputRef = useRef<HTMLInputElement>(null);
  const androidInputRef = useRef<HTMLInputElement>(null);
  
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({});
  const [isRoomLaunched, setIsRoomLaunched] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const inputRefs = {
    unity: unityInputRef,
    windows: windowsInputRef,
    macos: macosInputRef,
    android: androidInputRef,
  };

  const isVirtualSpaceSelected = selectedTags.includes(VIRTUAL_SPACE_TAG_ID);
  const { downloadRoomUrls, isLoading: isLoadingPackages } = useGetProjectPackages(projectId, isVirtualSpaceSelected);
  
  const requiredFieldsFilled = uploadedFiles.unity?.fileId && uploadedFiles.windows?.fileId;
  const isSaveDisabled = !requiredFieldsFilled || hasActiveUploads;

  useEffect(() => {
    if (!isLoadingPackages && downloadRoomUrls.length > 0) {
      setIsRoomLaunched(true);
      
      const filesFromServer: UploadedFiles = {};
      downloadRoomUrls.forEach(item => {
        const platformKey = getPlatformKey(item.platform);
        if (platformKey) {
          filesFromServer[platformKey] = {
            file: new File([], ''),
            fileId: item.downloadURL,
          };
        }
      });
      setUploadedFiles(filesFromServer);
    }
  }, [downloadRoomUrls, isLoadingPackages]);

  useEffect(() => {
    if (isVirtualSpaceSelected) {
      if (isRoomLaunched && downloadRoomUrls.length > 0) {
        onRequiredFieldsChange?.(true);
        onActiveUploadsChange?.(false);
      } else {
        onRequiredFieldsChange?.(!!requiredFieldsFilled);
        onActiveUploadsChange?.(!!hasActiveUploads);
      }
    } else {
      onRequiredFieldsChange?.(true);
      onActiveUploadsChange?.(false);
    }
  }, [isVirtualSpaceSelected, requiredFieldsFilled, hasActiveUploads, isRoomLaunched, downloadRoomUrls, onRequiredFieldsChange, onActiveUploadsChange]);

  const handleFileUpload = async (platform: string, file: File) => {
    try {
      setIsUploading(true);
      const fileId = await uploadFile(platform, file);
      
      if (fileId) {
        setUploadedFiles(prev => ({
          ...prev,
          [platform]: { file, fileId }
        }));
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled || !projectId) {
      return;
    }

    if (!requiredFieldsFilled) {
      toasts.error(t('room_launch.required_fields_error'));
      return;
    }

    try {
      setIsUploading(true);
      
      const platforms = [
        { key: 'unity', platformType: 0 },
        { key: 'windows', platformType: 1 },
        { key: 'macos', platformType: 2 },
        { key: 'android', platformType: 3 },
      ];

      const uploadPromises = platforms
        .filter(({ key }) => uploadedFiles[key as keyof UploadedFiles]?.fileId)
        .map(({ key, platformType }) => {
          const fileData = uploadedFiles[key as keyof UploadedFiles];
          
          return API.request()
            .endpoint(Endpoints.gateway)
            .url('metaverse/api/project/addpackage')
            .post()
            .body({
              projectId,
              fileId: fileData!.fileId,
              platformType,
            })
            .send();
        });

      await Promise.all(uploadPromises);
      
      setIsRoomLaunched(true);
      toasts.info(t('room_launch.room_launched'));
    } catch (error) {
      toasts.error(t('common.error.saving_data'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = () => {
    setIsRoomLaunched(false);
  };

  const getUploadedPlatforms = () => {
    if (isRoomLaunched && downloadRoomUrls.length > 0) {
      return downloadRoomUrls
        .filter(item => item.platform !== 0)
        .map(item => {
          const platformName = getPlatformName(item.platform);
          const platform = PLATFORMS.find(p => p.platformType === platformName);
          return platform?.label || platformName;
        });
    }
    
    return PLATFORMS
      .filter(platform => platform.key !== 'unity')
      .filter(platform => uploadedFiles[platform.key as keyof UploadedFiles]?.fileId)
      .map(platform => platform.label);
  };

  const getPlatformName = (platformType: number): string => {
    const platformMap: { [key: number]: string } = {
      0: 'Unity',
      1: 'Windows',
      2: 'MacOS',
      3: 'Android',
    };
    return platformMap[platformType] || 'Unknown';
  };

  const getPlatformKey = (platformType: number): keyof UploadedFiles | null => {
    const keyMap: { [key: number]: keyof UploadedFiles } = {
      0: 'unity',
      1: 'windows',
      2: 'macos',
      3: 'android',
    };
    return keyMap[platformType] || null;
  };

  return {
    isVirtualSpaceSelected,
    isRoomLaunched,
    isLoadingPackages,
    uploadedFiles,
    uploadProgress,
    hasActiveUploads,
    isSaveDisabled,
    inputRefs,
    uploadedPlatforms: getUploadedPlatforms(),
    handleFileUpload,
    handleCancelUpload,
    handleSave,
    handleEdit,
  };
};
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from '@brg/ui';
import { API, Endpoints } from '@brg/api';
import { VIRTUAL_SPACE_TAG_ID, UploadedFiles, PLATFORMS } from './constants';
import { useFileUpload } from './useFileUpload';
import { useGetProjectPackages } from './useGetProjectPackages';

interface UseRoomLaunchProps {
  selectedTags: string[];
  projectId?: string;
  onRequiredFieldsChange?: (isFilled: boolean) => void;
  onActiveUploadsChange?: (hasActiveUploads: boolean) => void;
}

export const useRoomLaunch = ({
  selectedTags,
  projectId,
  onRequiredFieldsChange,
  onActiveUploadsChange,
}: UseRoomLaunchProps) => {
  const { t } = useTranslation();
  const toasts = useToasts();
  
  const { uploadProgress, hasActiveUploads, handleFileUpload: uploadFile, handleCancelUpload } = useFileUpload();
  
  const unityInputRef = useRef<HTMLInputElement>(null);
  const windowsInputRef = useRef<HTMLInputElement>(null);
  const macosInputRef = useRef<HTMLInputElement>(null);
  const androidInputRef = useRef<HTMLInputElement>(null);
  
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({});
  const [isRoomLaunched, setIsRoomLaunched] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const inputRefs = {
    unity: unityInputRef,
    windows: windowsInputRef,
    macos: macosInputRef,
    android: androidInputRef,
  };

  const isVirtualSpaceSelected = selectedTags.includes(VIRTUAL_SPACE_TAG_ID);
  const { downloadRoomUrls, isLoading: isLoadingPackages } = useGetProjectPackages(projectId, isVirtualSpaceSelected);
  
  const requiredFieldsFilled = uploadedFiles.unity?.fileId && uploadedFiles.windows?.fileId;
  const isSaveDisabled = !requiredFieldsFilled || hasActiveUploads;

  useEffect(() => {
    if (!isLoadingPackages && downloadRoomUrls.length > 0) {
      setIsRoomLaunched(true);
      
      const filesFromServer: UploadedFiles = {};
      downloadRoomUrls.forEach(item => {
        const platformKey = getPlatformKey(item.platform);
        if (platformKey) {
          filesFromServer[platformKey] = {
            file: new File([], ''),
            fileId: item.downloadURL,
          };
        }
      });
      setUploadedFiles(filesFromServer);
    }
  }, [downloadRoomUrls, isLoadingPackages]);

  useEffect(() => {
    if (isVirtualSpaceSelected) {
      if (isRoomLaunched && downloadRoomUrls.length > 0) {
        onRequiredFieldsChange?.(true);
        onActiveUploadsChange?.(false);
      } else {
        onRequiredFieldsChange?.(!!requiredFieldsFilled);
        onActiveUploadsChange?.(!!hasActiveUploads);
      }
    } else {
      onRequiredFieldsChange?.(true);
      onActiveUploadsChange?.(false);
    }
  }, [isVirtualSpaceSelected, requiredFieldsFilled, hasActiveUploads, isRoomLaunched, downloadRoomUrls, onRequiredFieldsChange, onActiveUploadsChange]);

  const handleFileUpload = async (platform: string, file: File) => {
    try {
      setIsUploading(true);
      const fileId = await uploadFile(platform, file);
      
      if (fileId) {
        setUploadedFiles(prev => ({
          ...prev,
          [platform]: { file, fileId }
        }));
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled || !projectId) {
      return;
    }

    if (!requiredFieldsFilled) {
      toasts.error(t('room_launch.required_fields_error'));
      return;
    }

    try {
      setIsUploading(true);
      
      const platforms = [
        { key: 'unity', platformType: 0 },
        { key: 'windows', platformType: 1 },
        { key: 'macos', platformType: 2 },
        { key: 'android', platformType: 3 },
      ];

      const uploadPromises = platforms
        .filter(({ key }) => uploadedFiles[key as keyof UploadedFiles]?.fileId)
        .map(({ key, platformType }) => {
          const fileData = uploadedFiles[key as keyof UploadedFiles];
          
          return API.request()
            .endpoint(Endpoints.gateway)
            .url('metaverse/api/project/addpackage')
            .post()
            .body({
              projectId,
              fileId: fileData!.fileId,
              platformType,
            })
            .send();
        });

      await Promise.all(uploadPromises);
      
      setIsRoomLaunched(true);
      toasts.info(t('room_launch.room_launched'));
    } catch (error) {
      toasts.error(t('common.error.saving_data'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = () => {
    setIsRoomLaunched(false);
  };

  const getUploadedPlatforms = () => {
    if (isRoomLaunched && downloadRoomUrls.length > 0) {
      return downloadRoomUrls
        .filter(item => item.platform !== 0)
        .map(item => {
          const platformName = getPlatformName(item.platform);
          const platform = PLATFORMS.find(p => p.platformType === platformName);
          return platform?.label || platformName;
        });
    }
    
    return PLATFORMS
      .filter(platform => platform.key !== 'unity')
      .filter(platform => uploadedFiles[platform.key as keyof UploadedFiles]?.fileId)
      .map(platform => platform.label);
  };

  const getPlatformName = (platformType: number): string => {
    const platformMap: { [key: number]: string } = {
      0: 'Unity',
      1: 'Windows',
      2: 'MacOS',
      3: 'Android',
    };
    return platformMap[platformType] || 'Unknown';
  };

  const getPlatformKey = (platformType: number): keyof UploadedFiles | null => {
    const keyMap: { [key: number]: keyof UploadedFiles } = {
      0: 'unity',
      1: 'windows',
      2: 'macos',
      3: 'android',
    };
    return keyMap[platformType] || null;
  };

  return {
    isVirtualSpaceSelected,
    isRoomLaunched,
    isLoadingPackages,
    uploadedFiles,
    uploadProgress,
    hasActiveUploads,
    isSaveDisabled,
    inputRefs,
    uploadedPlatforms: getUploadedPlatforms(),
    handleFileUpload,
    handleCancelUpload,
    handleSave,
    handleEdit,
  };
};
